import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';

import { DELETE, GET, POST, POST_IMAGE, PUT } from '../service/base-api-service';

const initialState = {
  products: [],
  metadata: {
    categories: [],
    rangesByCategory: {},
    designsByRange: {},
  },
  isLoading: false,
  isMetadataLoading: false,
  isSaving: false,
  error: null,
};

const normalizeThunkError = (err, fallbackMessage) => ({
  status: err?.status,
  message: err?.message || err?.data?.message || fallbackMessage,
  data: err?.data,
});

const safeArray = (value) => (Array.isArray(value) ? value : []);

const normalizeDesign = (item) => {
  if (typeof item === 'string') {
    return {
      name: item,
      code: item.substring(0, 3).toUpperCase(),
      range: null,
    };
  }

  return {
    name: item?.name || item?.label || '',
    code: item?.code || item?.designCode || '',
    range: item?.range || null,
  };
};

const normalizePartNumber = (item) => ({
  pn: item?.pn || item?.partNumber || item?.part_number || '',
  capacity: item?.capacity ?? null,
});

const normalizeProduct = (item) => ({
  id: item?.id,
  category: item?.category || item?.categoryName || '',
  range: item?.range || item?.rangeName || '',
  design: item?.design || item?.designName || '',
  designCode: item?.designCode || item?.design_code || item?.code || '',
  name: item?.name || item?.productName || '',
  description: item?.description || '',
  image:
    item?.image ||
    item?.imageUrl ||
    item?.image_url ||
    item?.cloudinaryUrl ||
    '/product_images/placeholder.png',
  partNumbers: safeArray(item?.partNumbers || item?.part_numbers).map(normalizePartNumber),
});

const normalizeProductsList = (payload) => {
  const list = Array.isArray(payload?.results)
    ? payload.results
    : Array.isArray(payload?.items)
    ? payload.items
    : Array.isArray(payload)
    ? payload
    : [];

  return list.map(normalizeProduct);
};

const normalizeMetadata = (payload) => {
  const categories = safeArray(payload?.categories).map((item) => {
    if (typeof item === 'string') return item;
    return item?.name || item?.category || item?.label;
  }).filter(Boolean);

  const rangesByCategory = {};
  const rawRangesByCategory = payload?.rangesByCategory || payload?.ranges_by_category || {};
  Object.keys(rawRangesByCategory).forEach((category) => {
    rangesByCategory[category] = safeArray(rawRangesByCategory[category]).map((item) => {
      if (typeof item === 'string') return item;
      return item?.name || item?.range || item?.label;
    }).filter(Boolean);
  });

  const designsByRange = {};
  const rawDesignsByRange = payload?.designsByRange || payload?.designs_by_range || {};
  Object.keys(rawDesignsByRange).forEach((range) => {
    designsByRange[range] = safeArray(rawDesignsByRange[range]).map(normalizeDesign);
  });

  return {
    categories,
    rangesByCategory,
    designsByRange,
  };
};

export const fetchProducts = createAsyncThunk('products/fetchList', async (_, { rejectWithValue }) => {
  try {
    const data = await GET('v1/admin/products');
    return normalizeProductsList(data);
  } catch (err) {
    return rejectWithValue(normalizeThunkError(err, 'Unable to load products'));
  }
});

export const fetchProductMetadata = createAsyncThunk('products/fetchMetadata', async (_, { rejectWithValue }) => {
  try {
    const data = await GET('v1/admin/products/metadata');
    return normalizeMetadata(data || {});
  } catch (err) {
    return rejectWithValue(normalizeThunkError(err, 'Unable to load product metadata'));
  }
});

export const createProduct = createAsyncThunk(
  'products/create',
  async ({ payload, image }, { rejectWithValue }) => {
    try {
      const body = { ...payload };

      if (image) {
        const upload = await POST_IMAGE(image);
        const uploadedImageUrl = upload?.secure_url || upload?.url || null;
        body.imageUrl = uploadedImageUrl;
        body.image_url = uploadedImageUrl;
      } else if (body.imageUrl && !body.image_url) {
        body.image_url = body.imageUrl;
      }

      console.log('📤 Create Product - Request Body:', body);

      const data = await POST('v1/admin/products', body);
      return normalizeProduct(data || body);
    } catch (err) {
      return rejectWithValue(normalizeThunkError(err, 'Unable to create product'));
    }
  },
);

export const updateProduct = createAsyncThunk(
  'products/update',
  async ({ id, payload, image }, { rejectWithValue }) => {
    try {
      const body = { ...payload };

      console.log('📤 Update Product - Before Image Upload:', {
        id,
        body,
        hasImage: Boolean(image),
      });

      if (image) {
        const upload = await POST_IMAGE(image);
        const uploadedImageUrl = upload?.secure_url || upload?.url || null;
        body.imageUrl = uploadedImageUrl;
        body.image_url = uploadedImageUrl;
        console.log('📤 Update Product - After Image Upload:', {
          newImageUrl: body.imageUrl,
          new_image_url: body.image_url,
        });
      } else if (body.imageUrl && !body.image_url) {
        body.image_url = body.imageUrl;
      }

      console.log('📤 Update Product - Request Body:', body);

      const data = await PUT(`v1/admin/products/${id}`, body);
      console.log('✅ Update Product - Success:', data);
      return normalizeProduct(data || { ...body, id });
    } catch (err) {
      console.error('❌ Update Product - Error:', {
        status: err?.status,
        message: err?.message,
        data: err?.data,
      });
      return rejectWithValue(normalizeThunkError(err, 'Unable to update product'));
    }
  },
);

export const deleteProduct = createAsyncThunk('products/delete', async ({ id }, { rejectWithValue }) => {
  try {
    await DELETE(`v1/admin/products/${id}`);
    return { id };
  } catch (err) {
    return rejectWithValue(normalizeThunkError(err, 'Unable to delete product'));
  }
});

export const createProductCategory = createAsyncThunk(
  'products/createCategory',
  async ({ name }, { rejectWithValue }) => {
    try {
      const data = await POST('v1/admin/products/categories', { name });
      return {
        name: data?.name || data?.category || name,
      };
    } catch (err) {
      return rejectWithValue(normalizeThunkError(err, 'Unable to create category'));
    }
  },
);

export const updateProductCategory = createAsyncThunk(
  'products/updateCategory',
  async ({ id, name }, { rejectWithValue }) => {
    try {
      const data = await PUT(`v1/admin/products/categories/${id}`, { name });
      return {
        id,
        previousName: data?.previousName,
        name: data?.name || data?.category || name,
      };
    } catch (err) {
      return rejectWithValue(normalizeThunkError(err, 'Unable to update category'));
    }
  },
);

export const createProductRange = createAsyncThunk(
  'products/createRange',
  async ({ category, name }, { rejectWithValue }) => {
    try {
      const data = await POST('v1/admin/products/ranges', { category, name });
      return {
        category,
        name: data?.name || data?.range || name,
      };
    } catch (err) {
      return rejectWithValue(normalizeThunkError(err, 'Unable to create range'));
    }
  },
);

export const updateProductRange = createAsyncThunk(
  'products/updateRange',
  async ({ id, category, name }, { rejectWithValue }) => {
    try {
      const data = await PUT(`v1/admin/products/ranges/${id}`, { category, name });
      return {
        id,
        category,
        previousName: data?.previousName,
        name: data?.name || data?.range || name,
      };
    } catch (err) {
      return rejectWithValue(normalizeThunkError(err, 'Unable to update range'));
    }
  },
);

export const createProductDesign = createAsyncThunk(
  'products/createDesign',
  async ({ range, name, code }, { rejectWithValue }) => {
    try {
      const data = await POST('v1/admin/products/designs', { range, name, code });
      return normalizeDesign({
        name: data?.name || data?.design || name,
        code: data?.code || data?.designCode || code,
        range,
      });
    } catch (err) {
      return rejectWithValue(normalizeThunkError(err, 'Unable to create design'));
    }
  },
);

export const updateProductDesign = createAsyncThunk(
  'products/updateDesign',
  async ({ id, range, name, code }, { rejectWithValue }) => {
    try {
      const data = await PUT(`v1/admin/products/designs/${id}`, { range, name, code });
      return {
        id,
        range,
        previousName: data?.previousName,
        name: data?.name || data?.design || name,
        code: data?.code || data?.designCode || code,
      };
    } catch (err) {
      return rejectWithValue(normalizeThunkError(err, 'Unable to update design'));
    }
  },
);

export const updateProductPartNumbers = createAsyncThunk(
  'products/updatePartNumbers',
  async ({ productId, partNumbers }, { rejectWithValue }) => {
    try {
      const data = await PUT(`v1/admin/products/${productId}/part-numbers`, { partNumbers });
      return {
        id: productId,
        partNumbers: safeArray(data?.partNumbers || partNumbers).map(normalizePartNumber),
      };
    } catch (err) {
      return rejectWithValue(normalizeThunkError(err, 'Unable to update part numbers'));
    }
  },
);

const upsertProduct = (state, payload) => {
  const idx = state.products.findIndex((item) => item.id === payload.id);
  if (idx === -1) {
    state.products.unshift(payload);
  } else {
    state.products[idx] = payload;
  }
};

const mergeMetadataCategory = (state, categoryName) => {
  if (!categoryName) return;
  if (!state.metadata.categories.includes(categoryName)) {
    state.metadata.categories.push(categoryName);
  }
  if (!state.metadata.rangesByCategory[categoryName]) {
    state.metadata.rangesByCategory[categoryName] = [];
  }
};

export const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    resetProductsStore: () => initialState,
  },
  extraReducers: {
    [fetchProducts.pending]: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    [fetchProducts.rejected]: (state, action) => {
      state.isLoading = false;
      state.error = action.payload?.message || action.error?.message || 'Unable to load products';
    },
    [fetchProducts.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.products = safeArray(action.payload);
      state.error = null;
    },

    [fetchProductMetadata.pending]: (state) => {
      state.isMetadataLoading = true;
      state.error = null;
    },
    [fetchProductMetadata.rejected]: (state, action) => {
      state.isMetadataLoading = false;
      state.error = action.payload?.message || action.error?.message || 'Unable to load product metadata';
    },
    [fetchProductMetadata.fulfilled]: (state, action) => {
      state.isMetadataLoading = false;
      state.metadata = action.payload || initialState.metadata;
      state.error = null;
    },

    [createProduct.pending]: (state) => {
      state.isSaving = true;
      state.error = null;
    },
    [createProduct.rejected]: (state, action) => {
      state.isSaving = false;
      state.error = action.payload?.message || action.error?.message || 'Unable to create product';
    },
    [createProduct.fulfilled]: (state, action) => {
      state.isSaving = false;
      upsertProduct(state, action.payload);
      state.error = null;
    },

    [updateProduct.pending]: (state) => {
      state.isSaving = true;
      state.error = null;
    },
    [updateProduct.rejected]: (state, action) => {
      state.isSaving = false;
      state.error = action.payload?.message || action.error?.message || 'Unable to update product';
    },
    [updateProduct.fulfilled]: (state, action) => {
      state.isSaving = false;
      upsertProduct(state, action.payload);
      state.error = null;
    },

    [deleteProduct.pending]: (state) => {
      state.isSaving = true;
      state.error = null;
    },
    [deleteProduct.rejected]: (state, action) => {
      state.isSaving = false;
      state.error = action.payload?.message || action.error?.message || 'Unable to delete product';
    },
    [deleteProduct.fulfilled]: (state, action) => {
      state.isSaving = false;
      state.products = state.products.filter((item) => item.id !== action.payload?.id);
      state.error = null;
    },

    [createProductCategory.fulfilled]: (state, action) => {
      mergeMetadataCategory(state, action.payload?.name);
    },

    [createProductRange.fulfilled]: (state, action) => {
      const category = action.payload?.category;
      const range = action.payload?.name;
      if (!category || !range) return;
      mergeMetadataCategory(state, category);
      const ranges = state.metadata.rangesByCategory[category] || [];
      if (!ranges.includes(range)) {
        state.metadata.rangesByCategory[category] = [...ranges, range];
      }
      if (!state.metadata.designsByRange[range]) {
        state.metadata.designsByRange[range] = [];
      }
    },

    [createProductDesign.fulfilled]: (state, action) => {
      const design = action.payload;
      const range = design?.range;
      if (!range || !design?.name) return;
      const existing = state.metadata.designsByRange[range] || [];
      if (!existing.some((item) => item.name === design.name)) {
        state.metadata.designsByRange[range] = [...existing, { name: design.name, code: design.code }];
      }
    },

    [updateProductPartNumbers.fulfilled]: (state, action) => {
      const { id, partNumbers } = action.payload || {};
      const idx = state.products.findIndex((item) => item.id === id);
      if (idx !== -1) {
        state.products[idx].partNumbers = partNumbers;
      }
    },
  },
});

export const { resetProductsStore } = productsSlice.actions;

const getProductsStore = (state) => state.products;

export const getProducts = createSelector(getProductsStore, (state) => state.products || []);
export const getProductsLoading = createSelector(getProductsStore, (state) => state.isLoading);
export const getProductsSaving = createSelector(getProductsStore, (state) => state.isSaving);
export const getProductsError = createSelector(getProductsStore, (state) => state.error);
export const getProductMetadata = createSelector(getProductsStore, (state) => state.metadata || initialState.metadata);
export const getProductMetadataLoading = createSelector(getProductsStore, (state) => state.isMetadataLoading);

export default productsSlice.reducer;
